import { useCallback, useMemo } from 'react';
import { CommonActions } from '@react-navigation/native';
import { formatAmount } from 'utils';
import { clearTierBeforeDowngradingToBasic } from 'actions';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { useMainStackNavigation } from 'utils/types/navigationV6';
import { selectDefaultCurrency } from 'reducers/selectors';
import { selectFeaturesUsedWhenOnPremium } from 'reducers/selectors/premium';
import { tierThemes } from '../benefits';
import { selectShouldSeePremiumTrialScreen } from '../selectors';
const FEATURE_SCREEN = {
    spaces: 'DowngradeToBasicSpacesScreen',
    extraMembers: 'DowngradeToBasicExtraMembersScreen',
    nordVPN: 'DowngradeToBasicNordVPNScreen',
    subscriptions: 'DowngradeToBasicSubscriptionsScreen',
    rentReporting: 'DowngradeToBasicRentReportingScreen',
    budgeting: 'DowngradeToBasicBudgetingScreen',
    transactions: 'DowngradeToBasicTransactionsScreen',
    manualAccounts: 'DowngradeToBasicManualAccountsScreen',
    connections: 'DowngradeToBasicConnectionsScreen',
};
const DOWNGRADE_FLOW_ROUTES = new Set([
    'DowngradeToBasicInfoScreen',
    'LoginsUpgrade',
    'DowngradeToBasicSuccessScreen',
    ...Object.values(FEATURE_SCREEN),
]);
const useNavigateToMissedFeaturesAfterDowngradeToBasic = () => {
    const dispatch = useAppDispatch();
    const navigation = useMainStackNavigation();
    const features = useAppSelector(selectFeaturesUsedWhenOnPremium);
    const tierBeforeDowngradingToBasic = useAppSelector((store) => store.user.tierBeforeDowngradingToBasic);
    const currency = useAppSelector(selectDefaultCurrency);
    const isEligibleForTrial = useAppSelector((store) => tierBeforeDowngradingToBasic
        ? selectShouldSeePremiumTrialScreen(store, tierBeforeDowngradingToBasic)
        : false);
    const restartPlanTitle = useMemo(() => {
        if (!tierBeforeDowngradingToBasic) {
            return '';
        }
        const title = `Restart ${tierThemes[tierBeforeDowngradingToBasic].name}`;
        return isEligibleForTrial
            ? `${title} for ${formatAmount(0, currency, 2)}`
            : title;
    }, [tierBeforeDowngradingToBasic, isEligibleForTrial, currency]);
    const handleCloseFlow = useCallback(() => {
        dispatch(clearTierBeforeDowngradingToBasic());
        navigation.dispatch((state) => {
            const routes = state.routes.filter((route) => !DOWNGRADE_FLOW_ROUTES.has(route.name));
            return CommonActions.reset({
                ...state,
                routes,
                index: routes.length - 1,
            });
        });
    }, []);
    const handleUpgrade = useCallback(() => {
        navigation.navigate('EmmaProModal', {
            isTierRequired: true,
            tier: tierBeforeDowngradingToBasic,
            onUpgradeCallback: handleCloseFlow,
        });
    }, [handleCloseFlow, tierBeforeDowngradingToBasic]);
    const navigateToNext = useCallback((currentIndex) => {
        const steps = features?.length;
        if (steps) {
            const { routes } = navigation.getState();
            const routeMap = {};
            routes.forEach((r) => {
                routeMap[r.name] = true;
            });
            const nextFeature = features?.find((f) => !routeMap[FEATURE_SCREEN[f.type]]);
            if (nextFeature) {
                navigation.navigate(FEATURE_SCREEN[nextFeature.type], { index: currentIndex + 1 });
                return;
            }
        }
        navigation.navigate('DowngradeToBasicSuccessScreen', {
            index: currentIndex + 1,
        });
    }, [features]);
    return { navigateToNext, handleUpgrade, handleCloseFlow, restartPlanTitle };
};
export default useNavigateToMissedFeaturesAfterDowngradeToBasic;
